import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send } from 'lucide-react';
import MomentumLogo from '../assets/images/momentum-ai-logo.png';
import MomentumAILogoSmall from '../assets/images/Momentum AI Icon-color (1).png';
import { useTheme } from '../contexts/ThemeContext';
import { Dialog } from 'primereact/dialog';
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from 'remark-gfm';
import { Skeleton } from "primereact/skeleton";

type Message = {
  sender: 'user' | 'bot';
  text: string;
  followups?: string[];
};

type AmpOption = {
  id: string;
  title: string;
  description: string;
};

const AMP_OPTIONS: AmpOption[] = [
  {
    id: "support",
    title: "Support Assistant",
    description: "Get instant answers for support-related queries.",
  },
  {
    id: "ai-scans",
    title: "AI-Powered Scans",
    description: "Extract structured data from your documents.",
  },
  {
    id: "call-assistant",
    title: "Call Assistant",
    description: "Summarize customer calls using AI insights.",
  },
  {
    id: "report-assistant",
    title: "Report Assistant",
    description: "Auto-generate and analyze reports using AI.",
  },
  {
    id: "image-generator",
    title: "Image Generator",
    description: "Create images from text prompts using AI.",
  },
  {
    id: "trucking-market",
    title: "Trucking Market Appetite",
    description: "Analyze trucking insurance offerings with AI.",
  },
];


export default function Chatbot() {
  const { accentColor } = useTheme();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [showFooterCard, setShowFooterCard] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const navigate = useNavigate();
  const [selectedAmp, setSelectedAmp] = useState<AmpOption>(AMP_OPTIONS[0]);
  const [showAmpPopover, setShowAmpPopover] = useState(false);
  const ampSelectorRef = useRef<HTMLDivElement | null>(null);
  const ampPopoverRef = useRef<HTMLDivElement | null>(null);
  const [showSigninDialog, setShowSigninDialog] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [isSigningIn, setIsSigningIn] = useState(false);

  useEffect(() => {
    console.log('Current accent color:', accentColor);
  }, [accentColor]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        showAmpPopover &&
        ampSelectorRef.current &&
        ampPopoverRef.current &&
        !ampSelectorRef.current.contains(target) &&
        !ampPopoverRef.current.contains(target)
      ) {
        setShowAmpPopover(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAmpPopover]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userText = input;

    setMessages((prev) => [...prev, { sender: "user", text: userText }]);
    setInput("");
    setShowFooterCard(false);

    let botIndex = 0;

    setMessages((prev) => {
      botIndex = prev.length;
      return [...prev, { sender: "bot", text: "" }];
    });

    const formData = new FormData();
    formData.append("human_prompt", userText);
    formData.append("conversationId", "31db2299-cdba-4ca8-bd29-cd670cbe0a09");
    formData.append("file_paths", "[]");
    formData.append("ai_service", "NON-LOGIN-CHAT");

    try {
      const response = await fetch(
        "https://momentummyamp-stage.insurancegig.com/api/non-login-chat",
        {
          method: "POST",
          headers: {
            "X-API-KEY": "myamp_live_9f3a7c2e8b4d6a1c5e9f0a2b7d4c8e6f",
            Accept: "text/event-stream",
          },
          body: formData,
        }
      );

      if (!response.body) {
        throw new Error("Streaming not supported or empty response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer: string = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const events = buffer.split("\n\n");
        buffer = events.pop() ?? "";

        for (const event of events) {
          if (!event.startsWith("data:")) continue;

          const payload = event.replace("data:", "").trim();
          if (!payload) continue;

          const parsed: {
            Answer?: string;
            ResponseLoading?: boolean;
          } = JSON.parse(payload);

          // Stream text
          if (parsed.Answer) {
            setMessages((prev) => {
              const updated = [...prev];
              updated[botIndex] = {
                ...updated[botIndex],
                text: updated[botIndex].text + parsed.Answer,
              };
              return updated;
            });
          }

          if (parsed.ResponseLoading === false) {
            setMessages((prev) => {
              const updated = [...prev];
              const formatted = formatBotResponse(
                updated[botIndex].text
              );

              updated[botIndex] = {
                sender: "bot",
                text: formatted.message,
                followups: formatted.followups,
              };

              return updated;
            });

            reader.cancel();
            return;
          }
        }
      }
    } catch (error) {
      console.error(error);
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: "Something went wrong. Please try again." },
      ]);
    }
  };

  const formatBotResponse = (text: string) => {
    const start = "$$FollowupQuestions$$";
    const end = "$$FollowupQuestionsEND$$";

    if (!text.includes(start)) {
      return { message: text.trim(), followups: [] as string[] };
    }

    const [main, rest] = text.split(start);
    const followupBlock = rest.split(end)[0];

    const followups = followupBlock
      .replace(/\r?\n/g, " ")
      .split(/\d+\.\s+/)
      .map(q => q.trim())
      .filter(q => q.length > 0);

    return {
      message: main.trim(),
      followups,
    };
  };



  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSend();
  };

  const handleSettingsClick = () => {
    // navigate('/settings');
  };

  function generateGuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  const handleSignupClick = () => {
    navigate('/signup');
  };

  const handleSigninClick = () => {
    setShowSigninDialog(true);
  };

  const handleSigninSubmit = () => {

    const newErrors: { email?: string; password?: string } = {};

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    }

    if (!password.trim()) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    setIsSigningIn(true);

    let data = JSON.stringify({
      "workemail": email,
      "password": password
    });

    let config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: 'https://momentumampapi-stage.insurancegig.com/api/account/signin',
      headers: {
        'Content-Type': 'application/json'
      },
      data: data
    };

    axios.request(config)
      .then((response: any) => {
        setIsSigningIn(false);
        setShowSigninDialog(false);

        localStorage.setItem("authToken", response.data.authToken);
        localStorage.setItem("amsToken", response.data.amsToken);
        localStorage.setItem("userName", response.data.userName);
        localStorage.setItem("agency", response.data.agencyName);
        localStorage.setItem("NowcertsAgentId", response.data.nowCertsAgentId);
        localStorage.setItem("NCAgencyId", response.data.nowCertsAgencyId);

        const guid = generateGuid();
        navigate(`/supportbot/${guid}`);
      })
      .catch((error: any) => {
        setIsSigningIn(false);

        if (error.response && error.response.status === 401) {
          setErrors({ password: 'Invalid email or password' });
        } else {
          setErrors({ password: 'An error occurred. Please try again.' });
        }
      });
  };


  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleUploadClick = () => {
    setShowUploadModal(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setMessages([...messages, { sender: 'user', text: `Uploaded file: ${file.name}` }]);
    }
  };

  const handleMicClick = () => {
    const recognition = new (window as any).webkitSpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
    };
    recognition.onerror = (event: any) => {
    };
    recognition.start();
  };

  const handleAmpSelect = (amp: AmpOption) => {
    setSelectedAmp(amp);
    setShowAmpPopover(false);
  };

  const onHomeClicked = () => {
    setMessages([]);
  }

  return (
    <div className="hub-container">
      <div className="hub-bg-overlay" />

      <header className="hub-header">
        <div className="hub-logo-section" style={{ paddingRight: '24px' }}>
          <div className="hub-logo-icon" style={{ width: '170px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', cursor: 'pointer' }} onClick={onHomeClicked}>
            <img src={MomentumLogo} alt="Momentum Logo" style={{ width: '160px', height: '40px', objectFit: 'contain', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.05))' }} />
          </div>
        </div>
        <div className="hub-auth-buttons">
          <button className="hub-btn hub-btn-settings" title="Settings" style={{ background: 'rgba(var(--accent-color-rgb), 0.1)', border: '1px solid rgba(var(--accent-color-rgb), 0.2)', padding: '8px', borderRadius: '12px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', cursor: 'pointer' }} onClick={handleSettingsClick}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1c.18-.38.09-.82-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.09a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09c0 .66.39 1.25 1 1.51.38.18.82.09 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.09c0 .66.39 1.25 1 1.51.38.18.82.09 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.09c0 .66.39 1.25 1 1.51.38.18.82.09 1.82-.33z" /></svg>
          </button>
          <button className="hub-btn hub-btn-signin" title="Sign in" onClick={handleSigninClick}>Sign in</button>
          <button className="hub-btn hub-btn-signup" title="Sign up" onClick={handleSignupClick}>Sign up</button>
        </div>
      </header>

      <main className={`hub-main ${messages.length === 0 ? 'hub-main-empty' : ''}`} style={{ maxWidth: '1000px', width: '95%', margin: '0 auto' }}>
        {messages.length === 0 ? (
          <div className="hub-empty-state" style={{ textAlign: 'center', padding: '48px 24px' }}>
            <div className="hub-empty-icon" style={{ marginBottom: '24px' }}>
              <img src={MomentumAILogoSmall} alt="Momentum Logo" style={{ width: 95, height: 95, filter: 'drop-shadow(0 4px 12px rgba(var(--accent-color-rgb), 0.15))' }} />
            </div>
            <h2 className="hub-empty-title" style={{ color: '#1f2937', fontSize: '28px', fontWeight: '700', marginBottom: '12px', lineHeight: '1.3' }}>What do you want to know?</h2>
            <p className="hub-empty-subtitle" style={{ color: '#6b7280', fontSize: '16px', fontWeight: '400', lineHeight: '1.5', maxWidth: '480px', margin: '0 auto' }}>Ask me anything and I'll help you find the answers</p>
          </div>
        ) : (
          <div
            className="hub-messages"
            style={{ padding: "24px 16px", gap: "16px" }}
          >
            {messages.map((msg, idx) =>
              msg.sender === "user" ? (
                <div
                  key={idx}
                  className="hub-message hub-message-user"
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: "12px",
                    justifyContent: "flex-end",
                    background: "none",
                    boxShadow: "none",
                    padding: "8px 0",
                    marginBottom: "20px",
                  }}
                >
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(var(--accent-color-rgb), 0.1) 0%, rgba(var(--accent-color-rgb), 0.05) 100%)",
                      padding: "12px 16px",
                      borderRadius: "16px 16px 4px 16px",
                      maxWidth: "70%",
                      border: "1px solid rgba(var(--accent-color-rgb), 0.1)",
                    }}
                  >
                    <span
                      style={{
                        color: "#1f2937",
                        fontWeight: 500,
                        fontSize: "15px",
                        lineHeight: "1.4",
                      }}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                    </span>
                  </div>

                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      backgroundColor: accentColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "12px",
                      fontWeight: "600",
                      boxShadow: `0 2px 8px ${accentColor}40`,
                      flexShrink: 0,
                    }}
                  >
                    U
                  </div>
                </div>
              ) : (
                <div
                  key={idx}
                  className="hub-message hub-message-bot"
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: "12px",
                    justifyContent: "flex-start",
                    background: "none",
                    boxShadow: "none",
                    padding: "8px 0",
                    marginBottom: "20px",
                  }}
                >
                  <img
                    src={MomentumAILogoSmall}
                    alt="AI"
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(var(--accent-color-rgb), 0.15)",
                    }}
                  />
                  {(!msg.text || msg.text.length === 0) ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <Skeleton width="500px" height="1rem" />
                      <Skeleton width="300px" height="1rem" />
                      <Skeleton width="150px" height="1rem" />
                    </div>
                  ) : (
                    <div
                      style={{
                        background:
                          "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                        padding: "12px 16px",
                        borderRadius: "16px 16px 16px 4px",
                        maxWidth: "70%",
                        border: "1px solid rgba(var(--accent-color-rgb), 0.08)",
                      }}
                    >
                      <span style={{ color: "#374151", fontWeight: 500, fontSize: "15px", lineHeight: "1.4" }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                      </span>


                      {Array.isArray(msg.followups) && msg.followups.length > 0 && (
                        <div style={{ marginTop: "12px" }}>
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: "14px",
                              color: "#1f2937",
                              marginBottom: "6px",
                            }}
                          >
                            You may also ask:
                          </div>

                          <ul style={{ paddingLeft: "18px", margin: 0 }}>
                            {msg.followups.map((q, i) => (
                              <li
                                key={i}
                                style={{
                                  cursor: "pointer",
                                  color: accentColor,
                                  fontSize: "14px",
                                  marginBottom: "4px",
                                }}
                                onClick={() => setInput(q)} // optional
                              >
                                {q}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            )}

            <div ref={chatEndRef} />
          </div>

        )}

        <div className="hub-input-container">
          <div className="hub-input-inner">
            <div className="hub-input-left-section">
              <div className="amp-selector-wrap" ref={ampSelectorRef}>
                <button
                  type="button"
                  className="amp-selector-btn"
                  onClick={() => setShowAmpPopover((v) => !v)}
                  title="Choose assistant"
                >
                  <span className="amp-selector-icon">
                    {selectedAmp.id === 'support' && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                    )}
                    {selectedAmp.id === 'ai-scans' && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14,2 14,8 20,8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10,9 9,9 8,9" />
                      </svg>
                    )}
                    {selectedAmp.id === 'call-assistant' && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                    )}
                    {selectedAmp.id === 'report-assistant' && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="20" x2="18" y2="10" />
                        <line x1="12" y1="20" x2="12" y2="4" />
                        <line x1="6" y1="20" x2="6" y2="14" />
                      </svg>
                    )}
                    {selectedAmp.id === 'image-generator' && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21,15 16,10 5,21" />
                      </svg>
                    )}
                    {selectedAmp.id === 'trucking-market' && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="1" y="3" width="15" height="13" />
                        <polygon points="16,8 20,8 23,11 23,16 16,16 16,8" />
                        <circle cx="5.5" cy="18.5" r="2.5" />
                        <circle cx="18.5" cy="18.5" r="2.5" />
                      </svg>
                    )}
                  </span>
                  <span className="amp-selector-label">
                    {selectedAmp.title}
                  </span>
                  <span className="amp-selector-chevron">▾</span>
                </button>
                <div className="amp-selector-separator"></div>

                {showAmpPopover && (
                  <div
                    className="amp-selector-popover"
                    ref={ampPopoverRef}
                  >
                    <div className="amp-options-grid">
                      {AMP_OPTIONS.map((amp) => (
                        <button
                          key={amp.id}
                          type="button"
                          className={`amp-option-card ${amp.id === selectedAmp.id ? "active" : ""
                            }`}
                          onClick={() => handleAmpSelect(amp)}
                        >
                          <div className="amp-option-icon">
                            {amp.id === 'support' && (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                              </svg>
                            )}
                            {amp.id === 'ai-scans' && (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14,2 14,8 20,8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                                <polyline points="10,9 9,9 8,9" />
                              </svg>
                            )}
                            {amp.id === 'call-assistant' && (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                              </svg>
                            )}
                            {amp.id === 'report-assistant' && (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="20" x2="18" y2="10" />
                                <line x1="12" y1="20" x2="12" y2="4" />
                                <line x1="6" y1="20" x2="6" y2="14" />
                              </svg>
                            )}
                            {amp.id === 'image-generator' && (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <polyline points="21,15 16,10 5,21" />
                              </svg>
                            )}
                            {amp.id === 'trucking-market' && (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="1" y="3" width="15" height="13" />
                                <polygon points="16,8 20,8 23,11 23,16 16,16 16,8" />
                                <circle cx="5.5" cy="18.5" r="2.5" />
                                <circle cx="18.5" cy="18.5" r="2.5" />
                              </svg>
                            )}
                          </div>
                          <div className="amp-option-content">
                            <span className="amp-option-title">
                              {amp.title}
                            </span>
                            <p className="amp-option-desc">
                              {amp.description}
                            </p>
                          </div>
                          {amp.id === selectedAmp.id && (
                            <div className="amp-option-check">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <polyline points="20,6 9,17 4,12" />
                              </svg>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <button
                className="hub-attach-btn hub-attach-purple"
                onClick={handleUploadClick}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--accent-color)"
                  strokeWidth="2"
                >
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                </svg>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </button>
            </div>

            <input
              type="text"
              placeholder="What do you want to know?"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleInputKeyDown}
              className="hub-input"
            />

            <div className="hub-actions">
              <button
                onClick={handleSend}
                disabled={!input.trim()}
                className={`hub-send-btn ${input.trim() ? "hub-send-btn-active" : ""
                  }`}
                title="Send"
              >
                <Send size={22} />
              </button>
              <button
                className="hub-mic-btn"
                title="Voice input"
                onClick={handleMicClick}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--accent-color)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="9" y="2" width="6" height="12" rx="3" />
                  <path d="M5 10v2a7 7 0 0 0 14 0v-2" />
                  <line x1="12" y1="22" x2="12" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <p className="hub-footer-text" style={{ color: 'var(--text-tertiary)', fontSize: '13px', textAlign: 'center', marginTop: '24px', lineHeight: '1.4' }}>
          By messaging Momentum, you agree to our <span className="hub-link" style={{ color: 'var(--accent-color)', fontWeight: '500', cursor: 'pointer' }}>Terms</span> and <span className="hub-link" style={{ color: 'var(--accent-color)', fontWeight: '500', cursor: 'pointer' }}>Privacy Policy</span>
        </p>

        {showFooterCard && (
          <div className="hub-footer-card hub-footer-card-inline" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', border: '1px solid rgba(var(--accent-color-rgb), 0.15)', borderRadius: '16px', boxShadow: '0 8px 32px rgba(var(--accent-color-rgb), 0.1)' }}>
            <button className="hub-footer-card-close" onClick={() => setShowFooterCard(false)} title="Close" style={{ background: 'rgba(var(--accent-color-rgb), 0.1)', border: '1px solid rgba(var(--accent-color-rgb), 0.2)', borderRadius: '8px', padding: '6px', transition: 'all 0.2s ease' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <div className="hub-footer-card-header">
              <img src={MomentumAILogoSmall} alt="Momentum Logo" className="hub-footer-card-logo" />
              <span className="hub-footer-card-title">Momentum Updates</span>
            </div>
            <div className="hub-footer-card-content">
              <p className="hub-footer-card-update">🚀 <span className="hub-footer-card-update-bold">New features soon!</span><br /><span className="hub-footer-card-update-secondary">AI-powered productivity updates.</span></p>
              <div className="hub-footer-card-actions">
                <button className="hub-btn hub-btn-signin" onClick={handleSigninClick}>Sign in</button>
                <button className="hub-btn hub-btn-signup" onClick={handleSignupClick}>Sign up</button>
              </div>
            </div>
          </div>
        )}

        {showUploadModal && (
          <div className="hub-upload-modal-overlay">
            <div className="hub-upload-modal">
              <div className="hub-upload-modal-content">
                <svg className="hub-upload-modal-icon" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                </svg>
                <h3 className="hub-upload-modal-title">Sign in to Upload</h3>
                <p className="hub-upload-modal-description">Upload files to share with Momentum AI</p>
              </div>

              <div className="hub-upload-modal-buttons">
                <button
                  className="hub-btn hub-btn-signin hub-upload-modal-button"
                  onClick={() => { setShowUploadModal(false); handleSigninClick(); }}
                >
                  Sign in
                </button>
                <button
                  className="hub-btn hub-btn-signup hub-upload-modal-button"
                  onClick={() => { setShowUploadModal(false); handleSignupClick(); }}
                >
                  Sign up
                </button>
              </div>

              <button
                className="hub-upload-modal-cancel"
                onClick={() => setShowUploadModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </main>

      <Dialog
        header="Sign in to Momentum"
        visible={showSigninDialog}
        style={{ width: '400px' }}
        modal
        closable
        onHide={() => setShowSigninDialog(false)}
        draggable={false}
      >
        <div className="p-fluid ">
          <div className="form-row">
            <div className="form-field" style={{ width: "100%" }}>
              <label>Email <span style={{ color: '#d32f2f' }}>*</span></label>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrors((prev) => ({ ...prev, email: undefined }));
                }}
                placeholder="Email"
                className={errors.email ? 'input-error' : ''}
              />
              {errors.email && (
                <small className="error-text">{errors.email}</small>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-field" style={{ width: "100%" }}>
              <label>Password <span style={{ color: '#d32f2f' }}>*</span></label>
              <input
                type="password"
                name="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrors((prev) => ({ ...prev, password: undefined }));
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSigninSubmit();
                  }
                }}
                placeholder="Password"
                className={errors.password ? 'input-error' : ''}
              />
              {errors.password && (
                <small className="error-text">{errors.password}</small>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button
              className="hub-btn hub-btn-signin"
              onClick={handleSigninSubmit}
              disabled={isSigningIn}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: isSigningIn ? 0.7 : 1,
                cursor: isSigningIn ? 'not-allowed' : 'pointer'
              }}
            >
              {isSigningIn ? (
                <>
                  <span className="spinner" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </div>

        </div>
      </Dialog>


    </div>
  );
}

